// src/utils/consultation.js
//
// Pure logic for the doctor's consultation panel: next-step assembly, draft
// payloads / diffing / debouncing, and the sign sequence. See
// docs/visit-workflow-contract.md ("Visit").

import {
  buildLabTestSuggestion, dueDateInWeeks, isLabTestSuggestion,
} from './labTestSuggestion';

/** Visit next steps may only be these; URGENT/HIGH are the assembler's capped tiers. */
export const NEXT_STEP_PRIORITIES = ['MEDIUM', 'LOW', 'ROUTINE'];
export const DEFAULT_NEXT_STEP_PRIORITY = 'MEDIUM';
export const NEXT_STEP_MAX_CHARS = 500;
export const FOLLOW_UP_WEEKS = [2, 4, 8, 12];

export const isAllowedPriority = p => NEXT_STEP_PRIORITIES.includes(p);

/** Coerce anything else (HIGH, URGENT, junk) to the default rather than send it. */
export const restrictPriority = p => (isAllowedPriority(p) ? p : DEFAULT_NEXT_STEP_PRIORITY);

/** @returns {{description, priority, dueDate?}|null} null when the text is blank. */
export function buildNextStep({ description, priority, dueDate }) {
  const text = (description || '').trim().slice(0, NEXT_STEP_MAX_CHARS);
  if (!text) return null;
  const step = { description: text, priority: restrictPriority(priority) };
  if (dueDate) step.dueDate = dueDate;
  return step;
}

/** A lab-test suggestion as a visit next step (always capped to a visit priority). */
export function buildLabSuggestionStep({ markers = [], panelName, weeks, note }) {
  return buildNextStep({
    description: buildLabTestSuggestion({ markers, panelName, weeks, note }),
    priority: DEFAULT_NEXT_STEP_PRIORITY,
    dueDate: dueDateInWeeks(weeks),
  });
}

export const isLabSuggestionStep = step => isLabTestSuggestion(step?.description);

/** Comma/newline separated text → trimmed, de-duplicated codes. */
export function parseCodes(text) {
  const seen = new Set();
  return String(text || '').split(/[,\n;]+/).map(c => c.trim()).filter(c => {
    if (!c || seen.has(c.toLowerCase())) return false;
    seen.add(c.toLowerCase());
    return true;
  });
}

/** Editable form state from a server Visit. */
export function formFromVisit(visit) {
  return {
    diagnosisText: visit?.diagnosisText || '',
    diagnosisCodes: (visit?.diagnosisCodes || []).join(', '),
    adviceForPatient: visit?.adviceForPatient || '',
    followUpWeeks: visit?.followUpWeeks ?? '',
    nextSteps: (visit?.nextSteps || []).map(s => ({
      description: s.description || '', priority: restrictPriority(s.priority), dueDate: s.dueDate || '',
    })),
  };
}

/** PUT /doctor/visits/{id} body. Blank next steps are dropped. */
export function buildDraftPayload(form) {
  const weeks = form.followUpWeeks === '' || form.followUpWeeks == null ? null : Number(form.followUpWeeks);
  return {
    diagnosisText: form.diagnosisText ?? '',
    diagnosisCodes: parseCodes(form.diagnosisCodes),
    adviceForPatient: form.adviceForPatient ?? '',
    followUpWeeks: Number.isFinite(weeks) ? weeks : null,
    nextSteps: (form.nextSteps || []).map(buildNextStep).filter(Boolean),
  };
}

/** Canonical string for "did the draft change since the last save?". */
export const draftKey = form => JSON.stringify(buildDraftPayload(form));
export const isDraftDirty = (form, savedKey) => draftKey(form) !== savedKey;

/**
 * Trailing-edge debounce with flush/cancel, so Sign can force a pending save.
 * `fn` receives the latest arguments.
 */
export function createDebouncer(fn, ms, timers = {
  // Arrow wrappers, not `{ set: setTimeout }`: calling timers.set(...) would invoke
  // setTimeout with `this === timers`, which browsers reject ("Illegal invocation")
  // — that crashed the consultation panel on the first keystroke.
  set: (cb, delay) => setTimeout(cb, delay),
  clear: (h) => clearTimeout(h),
}) {
  let handle = null;
  let lastArgs = null;
  const run = () => { handle = null; const a = lastArgs; lastArgs = null; return fn(...a); };
  return {
    call(...args) { lastArgs = args; if (handle !== null) timers.clear(handle); handle = timers.set(run, ms); },
    flush() { if (handle === null) return undefined; timers.clear(handle); return run(); },
    cancel() { if (handle !== null) timers.clear(handle); handle = null; lastArgs = null; },
    pending: () => handle !== null,
  };
}

/**
 * Phase-1 sign sequence: every prescription draft first (in order), then the
 * visit. Stops at the first failure and never signs the visit after a failed
 * prescription, so the doctor can retry without a half-signed visit.
 *
 * @returns {Promise<{ok: boolean, signedPrescriptionIds: string[], visitSigned: boolean,
 *                    failure: null | {stage: 'prescription'|'visit', id: string|null, error: any}}>}
 */
export async function signConsultation({ prescriptionIds = [], signPrescription, signVisit }) {
  const signedPrescriptionIds = [];
  for (const id of prescriptionIds) {
    try {
      await signPrescription(id);
      signedPrescriptionIds.push(id);
    } catch (error) {
      return { ok: false, signedPrescriptionIds, visitSigned: false, failure: { stage: 'prescription', id, error } };
    }
  }
  try {
    await signVisit();
  } catch (error) {
    return { ok: false, signedPrescriptionIds, visitSigned: false, failure: { stage: 'visit', id: null, error } };
  }
  return { ok: true, signedPrescriptionIds, visitSigned: true, failure: null };
}

/** Plain-language explanation of a sign result, naming what did and did not get signed. */
export function describeSignFailure(result) {
  if (!result || result.ok) return '';
  const n = result.signedPrescriptionIds.length;
  const signed = n ? `${n} prescription${n === 1 ? ' was' : 's were'} signed. ` : '';
  if (result.failure?.stage === 'prescription') {
    return `${signed}A prescription could not be signed, so the visit was not signed. Nothing else changed; try again.`;
  }
  return `${signed}The visit could not be signed. ${n ? 'The signed prescription(s) are final; ' : ''}try signing again.`;
}

export const VITAL_FLAG_LABELS = {
  BP_HIGH: 'BP high', BP_LOW: 'BP low', SPO2_LOW: 'SpO₂ low',
  FEVER: 'Fever', TACHYCARDIA: 'Fast pulse', BRADYCARDIA: 'Slow pulse',
};

/** "2 min", "1 h 5 min"; null/negative → "just arrived". */
export function formatWaiting(minutes) {
  if (minutes == null || minutes < 1) return 'just arrived';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Compact vitals pairs for display, skipping absent values. */
export function vitalsSummary(v) {
  if (!v) return [];
  const rows = [];
  if (v.systolic != null && v.diastolic != null) rows.push(['BP', `${v.systolic}/${v.diastolic} mmHg`]);
  if (v.pulse != null) rows.push(['Pulse', `${v.pulse} bpm`]);
  if (v.spo2 != null) rows.push(['SpO₂', `${v.spo2}%`]);
  if (v.temperatureC != null) rows.push(['Temp', `${v.temperatureC} °C`]);
  if (v.weightKg != null) rows.push(['Weight', `${v.weightKg} kg`]);
  if (v.bmi != null) rows.push(['BMI', String(v.bmi)]);
  return rows;
}
